import type { ReactNode } from "react";
import { Icon } from "@iconify/react";
import { cn } from "@/shared/lib/utils";

export interface ComparisonColumn {
  key: string;
  /** Top cell: the record's name ("Option 1", "Earlier record") plus any tags. */
  header: ReactNode;
  /** One value per row label; a blank / "Not specified" value shows grey. */
  values: Record<string, ReactNode>;
  /** Bottom cell (source, recorded date, extra blocks). */
  footer?: ReactNode;
  /** Pickable column (radio) when onSelect is given. */
  selected?: boolean;
  onSelect?: () => void;
  ariaLabel?: string;
  testId?: string;
}

const EMPTY = new Set(["", "not specified", "not given", "not recorded", "not available"]);
const isEmpty = (value: ReactNode) => typeof value === "string" && EMPTY.has(value.trim().toLowerCase());

/**
 * The platform's record comparison (record conflicts, medication pairs):
 * ONE card, field names once down the left in grey, one column per record
 * with bold values, and rows that disagree highlighted across the whole card.
 * Each column is a real element (CSS subgrid), so it can be a radio.
 */
export function ComparisonTable({
  rows,
  differs,
  columns,
  label,
  footerLabel = "Source",
}: {
  rows: string[];
  differs: Set<string>;
  columns: ComparisonColumn[];
  /** Accessible name of the group. */
  label: string;
  footerLabel?: string;
}) {
  const selectable = columns.some((c) => c.onSelect);
  const hasFooter = columns.some((c) => c.footer);
  const rowCount = rows.length + 1 + (hasFooter ? 1 : 0);
  // The field names stay in view while the records scroll sideways.
  const labelCell = "sticky left-0 z-10 flex items-center border-r border-t border-[#E5E7EB] px-4 py-3 text-xs";

  return (
    <div
      role={selectable ? "radiogroup" : "list"}
      aria-label={label}
      // w-0 + min-w-full: exactly the card's width, and never widens the card
      // or the page around it, however many records there are; extra records
      // scroll sideways inside it instead.
      className="grid w-0 min-w-full overflow-x-auto rounded-xl border border-[#E5E7EB] bg-white"
      style={{
        // Every record column is at least as wide as it is when two records
        // are compared (half the space beside the names), so 3, 4 ... N
        // records keep that roomy width and scroll rather than squeeze.
        gridTemplateColumns:
          `minmax(88px, max-content) repeat(${columns.length}, minmax(max(150px, calc((100% - 160px) / 2)), 1fr))`,
        gridTemplateRows: `repeat(${rowCount}, auto)`,
      }}
    >
      {/* Left column: the field names, once. */}
      <div className="sticky left-0 z-10 border-r border-[#E5E7EB] bg-[#F9FAFB]" style={{ gridColumn: 1, gridRow: 1 }}>
        <span className="sr-only">Detail</span>
      </div>
      {rows.map((name, i) => {
        const isDifferent = differs.has(name);
        return (
          <div
            key={name}
            data-differs={isDifferent || undefined}
            className={cn(labelCell, isDifferent ? "bg-[#FEF6DC] font-semibold text-[#946200]" : "bg-white font-medium text-[#6B7280]")}
            style={{ gridColumn: 1, gridRow: i + 2 }}
          >
            {name}
          </div>
        );
      })}
      {hasFooter && (
        <div className={cn(labelCell, "bg-white font-medium text-[#6B7280]")} style={{ gridColumn: 1, gridRow: rowCount }}>
          {footerLabel}
        </div>
      )}

      {/* One column per record, spanning every row (subgrid keeps the rows aligned). */}
      {columns.map((column, index) => {
        const selected = Boolean(column.selected);
        const cells = (
          <>
            {/* The selected column is marked only by its faded-red header, never a border. */}
            <div className={cn("px-4 py-3", selected ? "bg-[#FFF2F4]" : "bg-[#F9FAFB]")}>
              {column.onSelect ? (
                <div className="flex items-start gap-1.5">
                  <Icon
                    icon={selected ? "lucide:circle-dot" : "lucide:circle"}
                    className={cn("mt-0.5 h-4 w-4 shrink-0", selected ? "text-[#E4002B]" : "text-[#9CA3AF]")}
                  />
                  <div className="min-w-0 flex-1">
                    {column.header}
                    {/* Says it's a choice: the cards otherwise read like plain information. */}
                    <span className={cn("mt-1 block text-xs font-semibold", selected ? "text-[#E4002B]" : "text-[#6B7280]")}>
                      {selected ? "Your pick" : "Tap to pick this one"}
                    </span>
                  </div>
                </div>
              ) : (
                column.header
              )}
            </div>
            {rows.map((name) => {
              const value = column.values[name];
              const isDifferent = differs.has(name);
              return (
                <div
                  key={name}
                  data-differs={isDifferent || undefined}
                  className={cn("flex items-center break-words border-t border-[#E5E7EB] px-4 py-3 text-sm", isDifferent && "bg-[#FEF6DC]")}
                >
                  {value === undefined || value === null || isEmpty(value) ? (
                    <span className="text-[#9CA3AF]">{typeof value === "string" && value.trim() ? value : "Not given"}</span>
                  ) : (
                    <span className="font-semibold text-[#111827]">{value}</span>
                  )}
                </div>
              );
            })}
            {hasFooter && (
              <div className="border-t border-[#E5E7EB] px-4 py-3 text-[11px] leading-4 text-[#6B7280]">{column.footer}</div>
            )}
          </>
        );
        const className = "relative grid grid-rows-subgrid border-l border-[#E5E7EB] text-left";
        const style = { gridColumn: index + 2, gridRow: `1 / span ${rowCount}` };
        return column.onSelect ? (
          <button
            key={column.key}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={column.ariaLabel}
            data-testid={column.testId}
            onClick={column.onSelect}
            className={cn(className, "cursor-pointer transition-colors", !selected && "hover:bg-[#FFF9FA]")}
            style={style}
          >
            {cells}
          </button>
        ) : (
          <div key={column.key} role="listitem" data-testid={column.testId} className={className} style={style}>
            {cells}
          </div>
        );
      })}
    </div>
  );
}
